import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "./thunks";
import { Supplier, SupplierData } from "@/types";
import { toast } from "react-toastify";

interface PaginatedSuppliers {
  data: Supplier[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface SuppliersState {
  suppliers: PaginatedSuppliers;
  loading: boolean;
  error: string | null;
  fetched: boolean;
}

const initialState: SuppliersState = {
  suppliers: { data: [] },
  loading: false,
  error: null,
  fetched: false,
};

const suppliersSlice = createSlice({
  name: "suppliers",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.suppliers = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSuppliers.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.fetched = true;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.suppliers = { data: payload.data };
          } else {
            // Dados paginados
            state.suppliers = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )

      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })

      // Create
      .addCase(createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSupplier.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.suppliers.data.push(action.payload.data);
          toast.success("Fornecedor cadastrado com sucesso!");
        },
      )
      .addCase(createSupplier.rejected, (state, action) => {
        console.log((action.payload as { error: any }).error?.message);

        state.loading = false;
        state.error = (action.payload as { error: any }).error?.message;
        toast.error(
          (action.payload as { error: any }).error?.message ??
            "Não foi possível cadastrar o fornecedor, tente novamente!",
        );
      })

      // Update
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSupplier.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.suppliers.data.findIndex(
            (s) => s.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.suppliers.data[index] = action.payload.data;
          }
          toast.success("Fornecedor atualizado com sucesso!");
        },
      )
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: any }).error?.message;
        toast.error(
          (action.payload as { error: any }).error?.message ??
            "Não foi possível atualizar o fornecedor, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteSupplier.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.suppliers.data = state.suppliers.data.filter(
            (s) => String(s.id) != action.payload.data.id,
          );
          toast.success("Fornecedor excluído com sucesso!");
        },
      )
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { error: any }).error?.message;
        toast.error(
          (action.payload as { error: any }).error?.message ??
            "Não foi possível excluir o fornecedor, tente novamente!",
        );
      });
  },
});

export const { clearSuppliers, resetFetched } = suppliersSlice.actions;
export default suppliersSlice.reducer;
